import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import ToggleTypeAccordionItem from '../toggle-type-accordion-item';
import styles from './styles.module.scss';
import { ChevronUpIcon } from '@/components/icons';
import type { ListTypeListItem } from '@/types/list-type';
import { useTranslation } from 'react-i18next';
import useAccessibilityTTS from '@/hooks/useAccessibilityTTS';

interface ToggleTypeAccordionListProps {
    items: ListTypeListItem[];
    isHorizontal: boolean;
}

const ToggleTypeAccordionList = ({ items, isHorizontal }: ToggleTypeAccordionListProps) => {
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleToggle = useCallback((id: string) => {
        setExpandedId((prev) => (prev === id ? null : id));
    }, []);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setShowScrollTop(e.currentTarget.scrollTop > 20);
    }, []);

    const handleScrollTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        speak(t('list_type.detail.tts.moved_to_top'));
    }, [speak, t]);

    return (
        <div className={styles.wrapper}>
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className={clsx(styles.list, 'scrollbar-custom-gray-99938f scrollbar-w-6', {
                    [styles['list--horizontal']]: isHorizontal,
                    [styles['list--vertical']]: !isHorizontal,
                })}
                data-region-nav
                data-region-order={2}
            >
                {items.map((item, index) => (
                    <ToggleTypeAccordionItem
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        content={item.contents}
                        categoryName={item.categoryName || ''}
                        isExpanded={expandedId === item.id}
                        onToggle={handleToggle}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        index={index}
                    />
                ))}
            </div>

            {showScrollTop && (
                <button
                    type="button"
                    className={clsx(styles['scroll-top'], 'focus-border')}
                    onClick={handleScrollTop}
                    aria-label={t('list_type.detail.tts.go_to_top')}
                    data-role={t('common.data_role.button')}
                    tabIndex={3}
                    data-interactive
                    data-region-nav
                    data-region-order={3}
                >
                    <ChevronUpIcon className={styles['scroll-top__icon']} />
                </button>
            )}
        </div>
    );
};

export default ToggleTypeAccordionList;
